const ITEM_EDITOR_SCROLLER_ID = 'item-editor-body';
const ACCORDION_OPEN_FALLBACK_MS = 250;

interface OpenSectionScrollMetrics {
    scrollTop: number;
    viewportHeight: number;
    /** Distance from the scroller's top edge to the section's top edge. */
    sectionTop: number;
    sectionHeight: number;
    maxScrollTop: number;
}

/**
 * Scroll offset that shows as much of an open section as possible.
 * A section taller than the viewport aligns to the top. A shorter section
 * is centered so the content above and below it stays visible.
 */
const getOpenSectionScrollTop = ({
    scrollTop,
    viewportHeight,
    sectionTop,
    sectionHeight,
    maxScrollTop,
}: OpenSectionScrollMetrics): number => {
    const sectionOffsetTop = scrollTop + sectionTop;
    const unclamped = sectionHeight >= viewportHeight
        ? sectionOffsetTop
        : sectionOffsetTop - (viewportHeight - sectionHeight) / 2;
    const upper = Math.max(0, maxScrollTop);

    return Math.min(Math.max(0, unclamped), upper);
};

const scrollOpenSectionIntoView = (sectionEl: HTMLElement): void => {
    const scroller = sectionEl.closest(`#${ITEM_EDITOR_SCROLLER_ID}`);
    if (!(scroller instanceof HTMLElement)) {
        return;
    }

    const scrollerRect = scroller.getBoundingClientRect();
    const sectionRect = sectionEl.getBoundingClientRect();
    const paddingTop = Number.parseFloat(window.getComputedStyle(scroller).paddingTop) || 0;
    const nextScrollTop = getOpenSectionScrollTop({
        scrollTop: scroller.scrollTop,
        viewportHeight: scroller.clientHeight,
        sectionTop: sectionRect.top - scrollerRect.top - paddingTop,
        sectionHeight: sectionEl.offsetHeight,
        maxScrollTop: scroller.scrollHeight - scroller.clientHeight,
    });

    scroller.scrollTo({ top: nextScrollTop, behavior: 'auto' });
};

const hasHeightTransition = (element: HTMLElement): boolean => {
    const style = window.getComputedStyle(element);
    const hasDuration = style.transitionDuration.split(',').some((part) => Number.parseFloat(part) > 0);
    if (!hasDuration) {
        return false;
    }

    return style.transitionProperty.split(',').some((part) => {
        const name = part.trim();
        return name === 'all' || name === 'height';
    });
};

/**
 * Scrolls once the open accordion content has its final height.
 * Editor sections are not height-animated, so that path scrolls immediately.
 * When a height transition is running, wait for it, with a short timeout
 * in case `transitionend` never fires.
 */
const scrollOpenSectionAfterOpenAnimation = (
    sectionEl: HTMLElement,
    onScrolled: () => void,
): (() => void) => {
    const content = sectionEl.querySelector('[role="region"]');
    if (!(content instanceof HTMLElement) || !hasHeightTransition(content)) {
        scrollOpenSectionIntoView(sectionEl);
        onScrolled();
        return () => undefined;
    }

    let settled = false;
    let timeoutId = 0;
    const scroll = () => {
        if (settled) {
            return;
        }
        settled = true;
        window.clearTimeout(timeoutId);
        scrollOpenSectionIntoView(sectionEl);
        onScrolled();
    };

    const onTransitionEnd = (event: Event) => {
        if (!(event instanceof TransitionEvent) || event.propertyName !== 'height') {
            return;
        }
        scroll();
    };

    timeoutId = window.setTimeout(scroll, ACCORDION_OPEN_FALLBACK_MS);
    content.addEventListener('transitionend', onTransitionEnd);

    return () => {
        settled = true;
        window.clearTimeout(timeoutId);
        content.removeEventListener('transitionend', onTransitionEnd);
    };
};

export {
    getOpenSectionScrollTop,
    scrollOpenSectionAfterOpenAnimation,
    scrollOpenSectionIntoView,
};
export type { OpenSectionScrollMetrics };
